import { Skeleton, SkeletonHead, SkeletonCardGrid } from '@shared/ui';

/**
 * The APPLICATION-WIDE floor — the state the guard shows while the boot refresh
 * is in flight, and the fallback a route added without a skeleton of its own
 * falls back to.
 *
 * It stands in for the WHOLE SHELL, not for a page: `AuthGuard` returns this
 * INSTEAD of `<AppShell>`, so a floor that drew page blocks alone painted a
 * bare column with no navbar and no footer, and the shell then dropped in over
 * it — a layout change on every cold load, which is precisely the shift a
 * placeholder exists to prevent. The bar and the footer band below are the
 * shell's own boxes at the shell's own heights.
 *
 * The real `Topbar` is NOT mounted here, and that is deliberate rather than
 * lazy: it carries the account menu and the notification handle, and both read
 * as soon as they mount. Rendering them during the boot refresh would fire
 * authenticated reads from a session that does not exist yet and answer a
 * question the refresh is still asking.
 *
 * `aria-busy` is announced ONCE, here on the region, and never on the blocks
 * inside it: each of those is `aria-hidden`, because a placeholder has no text
 * to read. The visible label is the polite announcement a screen reader gets;
 * the shapes are pixels only.
 */
export function PendingRoute() {
  return (
    <div
      role="status"
      aria-busy="true"
      className="flex min-h-dvh flex-col bg-bg pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] text-ink md:pb-0"
    >
      <span className="visually-hidden">جارٍ التحميل</span>

      {/* The top bar, at its own `px-gutter py-3` rhythm: the brand block at
          the reading start and the control cluster at the reading end. */}
      <div aria-hidden="true" className="flex items-center gap-3 border-b border-transparent px-gutter py-3">
        <Skeleton className="h-6 w-6 shrink-0 rounded-sm" />
        <Skeleton className="h-4 w-24" />
        <span className="ms-auto" />
        <Skeleton className="h-11 w-11 rounded-sm" />
        <Skeleton className="h-11 w-11 rounded-sm" />
        <Skeleton className="h-9 w-9 rounded-pill" />
      </div>

      <div className="container flex flex-[1_0_auto] flex-col">
        <main className="flex-[1_0_auto] self-stretch py-10">
          <SkeletonHead />

          {/* One panel where the surface's own leading block would stand, then
              the generic card ladder — the two shapes every unresolved route in
              this area collapses into. */}
          <Skeleton className="h-40 w-full rounded-md" />
          <SkeletonCardGrid count={3} cover={false} />
        </main>
      </div>

      {/* The footer band, so the page ends where the shell's page ends. */}
      <div aria-hidden="true" className="border-t border-border px-gutter py-8">
        <Skeleton className="h-4 w-40" />
      </div>
    </div>
  );
}
